import { useState } from 'react';
import { Edit2, Loader2, X, FileText } from 'lucide-react';
import { useEditContractInfo } from '@/features/contracts/hooks/useContracts';
import type { Person, VehicleData } from '@/features/contracts/types/contract.types';
import { normalizeNameSpaces } from '@/utils/nameHelpers';

interface EditContractInfoModalProps {
  contractId: number;
  beneficiary?: Person;
  vehicle?: VehicleData;
  isOpen: boolean;
  onClose: () => void;
}

export function EditContractInfoModal({
  contractId,
  beneficiary,
  vehicle,
  isOpen,
  onClose,
}: EditContractInfoModalProps) {
  const { mutate, isLoading } = useEditContractInfo();

  const [firstName, setFirstName] = useState(beneficiary?.firstName || '');
  const [lastName, setLastName] = useState(beneficiary?.lastName || '');
  const [email, setEmail] = useState(beneficiary?.email || '');
  const [phoneNumber, setPhoneNumber] = useState(beneficiary?.phoneNumber || '');
  const [registration, setRegistration] = useState(vehicle?.registration || '');
  const [kmStart, setKmStart] = useState(vehicle?.kmStart || '');
  const [vehicleSellingPrice, setVehicleSellingPrice] = useState(vehicle?.price || '');

  if (!isOpen) return null;

  const handleSubmit = (regeneratePdf: boolean) => {
    mutate(
      {
        id: contractId,
        data: {
          beneficiary: {
            firstName: normalizeNameSpaces(firstName),
            lastName: normalizeNameSpaces(lastName),
            email,
            phoneNumber,
          },
          vehicle: { registration, kmStart, vehicleSellingPrice },
          regeneratePdf,
        },
      },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/50">
      <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-lg tw-w-full tw-mx-4 tw-max-h-[90vh] tw-overflow-y-auto">
        {/* Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-px-6 tw-py-4 tw-border-b tw-border-gray-200">
          <div className="tw-flex tw-items-center tw-gap-2">
            <Edit2 className="tw-w-5 tw-h-5 tw-text-affinicar-primary" />
            <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-m-0">Modifier le contrat</h3>
          </div>
          <button onClick={onClose} className="tw-p-1 tw-rounded hover:tw-bg-gray-100">
            <X className="tw-w-5 tw-h-5 tw-text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="tw-px-6 tw-py-4 tw-space-y-5">
          {/* Bénéficiaire */}
          <div>
            <p className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-3">Bénéficiaire</p>
            <div className="tw-grid tw-grid-cols-2 tw-gap-3">
              <div>
                <label className="tw-block tw-text-xs tw-text-gray-500 tw-mb-1">Prénom</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary/20 focus:tw-border-affinicar-primary"
                />
              </div>
              <div>
                <label className="tw-block tw-text-xs tw-text-gray-500 tw-mb-1">Nom</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary/20 focus:tw-border-affinicar-primary"
                />
              </div>
              <div>
                <label className="tw-block tw-text-xs tw-text-gray-500 tw-mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary/20 focus:tw-border-affinicar-primary"
                />
              </div>
              <div>
                <label className="tw-block tw-text-xs tw-text-gray-500 tw-mb-1">Téléphone</label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary/20 focus:tw-border-affinicar-primary"
                />
              </div>
            </div>
          </div>

          {/* Véhicule */}
          <div>
            <p className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-3">Véhicule</p>
            <div className="tw-grid tw-grid-cols-2 tw-gap-3">
              <div>
                <label className="tw-block tw-text-xs tw-text-gray-500 tw-mb-1">Immatriculation</label>
                <input
                  type="text"
                  value={registration}
                  onChange={(e) => setRegistration(e.target.value)}
                  className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary/20 focus:tw-border-affinicar-primary"
                />
              </div>
              <div>
                <label className="tw-block tw-text-xs tw-text-gray-500 tw-mb-1">Kilométrage</label>
                <input
                  type="text"
                  value={kmStart}
                  onChange={(e) => setKmStart(e.target.value)}
                  className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary/20 focus:tw-border-affinicar-primary"
                />
              </div>
              <div className="tw-col-span-2">
                <label className="tw-block tw-text-xs tw-text-gray-500 tw-mb-1">Prix de vente du véhicule (€)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={vehicleSellingPrice}
                  onChange={(e) => setVehicleSellingPrice(e.target.value)}
                  className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary/20 focus:tw-border-affinicar-primary"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer — 2 boutons */}
        <div className="tw-px-6 tw-py-4 tw-border-t tw-border-gray-200 tw-flex tw-flex-col tw-gap-2">
          <button
            onClick={() => handleSubmit(false)}
            disabled={isLoading}
            className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-2.5 tw-bg-affinicar-primary tw-text-white tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-affinicar-dark tw-transition-colors disabled:tw-opacity-50"
          >
            {isLoading ? <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" /> : <Edit2 className="tw-w-4 tw-h-4" />}
            Modifier les informations
          </button>
          <button
            onClick={() => handleSubmit(true)}
            disabled={isLoading}
            className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-2.5 tw-bg-orange-500 tw-text-white tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-orange-600 tw-transition-colors disabled:tw-opacity-50"
          >
            {isLoading ? <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" /> : <FileText className="tw-w-4 tw-h-4" />}
            Modifier les informations et regénérer le PDF
          </button>
        </div>
      </div>
    </div>
  );
}
